/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useEffect, useRef } from 'react';

const LONG_KEY_PRESS_THRESHOLD_TIME = 0.5 * 1000;

export function useKeyPressAction(
    keys: string[],
    callback: Function,
    enabled = true,
    longPress = false,
) {
    const keyPressStart = useRef(new Map());

    useEffect(() => {
        if (!enabled) return;

        const normalizedKeys = new Set(keys.map((k) => k.toLowerCase()));

        const handleKeyDown = (event: KeyboardEvent) => {
            const key = event.key.toLowerCase();
            if (normalizedKeys.has(key) && !keyPressStart.current.has(key)) {
                keyPressStart.current.set(key, Date.now());
            }
        };

        const handleKeyUp = (event: KeyboardEvent) => {
            const key = event.key.toLowerCase();
            const startTime = keyPressStart.current.get(key);
            keyPressStart.current.delete(key);

            if (!startTime || !normalizedKeys.has(key)) return;

            const pressDuration = Date.now() - startTime;

            if (!longPress && pressDuration <= LONG_KEY_PRESS_THRESHOLD_TIME) {
                callback();
            } else if (longPress && pressDuration > LONG_KEY_PRESS_THRESHOLD_TIME) {
                callback();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        // eslint-disable-next-line consistent-return
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [keys, callback, longPress, enabled]);
}
